/* SchriftjeQuickDialog — inline schriftje-editor voor de leidster vanuit de
   Presentielijst. Toont recente entries + snelle invoer + AI-assistent. */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Sparkles, Smile, Meh, Frown, Camera, Mic } from "lucide-react";
import { toast } from "sonner";
import { formatDate, localToday } from "@/lib/format";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import VoiceRecorder from "@/components/VoiceRecorder";
import MultiKindSchriftjeDialog, { countKnownKinderenInTranscript } from "@/components/MultiKindSchriftjeDialog";

const STEMMINGEN = [
  { v: "blij", label: "Blij", icon: Smile },
  { v: "neutraal", label: "Neutraal", icon: Meh },
  { v: "verdrietig", label: "Verdrietig", icon: Frown },
  { v: "moe", label: "Moe", icon: Meh },
];

function initForm() {
  return {
    datum: localToday(),
    stemming: "blij",
    activiteiten: "",
    eten: "",
    slaap: "",
    verschoning_luier: 0,
    hoogtepunt: "",
    notitie_voor_ouder: "",
  };
}

export default function SchriftjeQuickDialog({ open, onClose, kind }) {
  const [recent, setRecent] = useState([]);
  const [form, setForm] = useState(initForm());
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [groupKinderen, setGroupKinderen] = useState([]);
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiTranscript, setMultiTranscript] = useState("");

  useEffect(() => {
    if (!open || !kind?.id) return;
    setForm(initForm());
    api.get("/schriftje", { params: { kind_id: kind.id, limit: 5 } })
      .then(r => setRecent(r.data || []))
      .catch(() => setRecent([]));
    // Haal groepgenoten op voor multi-kind detectie (zelfde group_id)
    if (kind?.group_id) {
      api.get("/children", { params: { group_id: kind.group_id } })
        .then(r => setGroupKinderen(r.data || []))
        .catch(() => setGroupKinderen([]));
    }
  }, [open, kind?.id, kind?.group_id]);

  const handleTranscribed = (text) => {
    if (!text || !text.trim()) {
      toast.error("Lege opname — geen tekst herkend");
      return;
    }
    setForm(f => ({
      ...f,
      activiteiten: (f.activiteiten ? f.activiteiten + " " : "") + text,
    }));
    // Multi-kind auto-detectie op basis van groepgenoten
    if (groupKinderen.length >= 2) {
      const known = countKnownKinderenInTranscript(text, groupKinderen);
      if (known >= 2) {
        setMultiTranscript(text);
        setMultiOpen(true);
        toast.info(`${known} kinderen herkend — controleer per kind`);
      }
    }
  };

  const runAI = async () => {
    setAiBusy(true);
    try {
      const payload = {
        kind_naam: `${kind.voornaam} ${kind.achternaam}`,
        datum: form.datum,
        stemming: form.stemming,
        eten: form.eten,
        slapen: form.slaap,
        activiteiten: form.activiteiten,
        ruwe_notities: form.notitie_voor_ouder,
        gewenste_taal: "Nederlands",
      };
      const r = await api.post("/schriftje/ai-assist", payload);
      const tekst = r.data?.nederlands || "";
      setForm(f => ({
        ...f,
        activiteiten: tekst || f.activiteiten,
        hoogtepunt: (r.data?.activiteiten?.[0]?.activiteit) || f.hoogtepunt,
      }));
      toast.success("AI-tekst gegenereerd — controleer voor je opslaat");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI-assistent niet bereikbaar");
    } finally { setAiBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/schriftje", {
        kind_id: kind.id,
        datum: form.datum,
        stemming: form.stemming,
        activiteiten: form.activiteiten,
        eten: form.eten,
        slaap: form.slaap,
        verschoning_luier: parseInt(form.verschoning_luier || 0),
        hoogtepunt: form.hoogtepunt,
        notitie_voor_ouder: form.notitie_voor_ouder,
        verzonden: true,
      });
      toast.success("Schriftje verzonden naar ouder");
      setForm(initForm());
      const r = await api.get("/schriftje", { params: { kind_id: kind.id, limit: 5 } });
      setRecent(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  if (!kind) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="schriftje-quick-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[color:var(--sage)]" />
            Schriftje — {kind.voornaam} {kind.achternaam}
          </DialogTitle>
          <DialogDescription>
            Snelle invoer met optionele AI-hulp. Geef alleen echte waarnemingen mee; AI vult daar de rest omheen.
          </DialogDescription>
        </DialogHeader>

        {/* Recente entries */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Recent ({recent.length})</div>
          {recent.length === 0 && <div className="text-xs text-neutral-500 mb-3">Nog geen schriftje-entries.</div>}
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {recent.map(s => (
              <div key={s.id} className="p-2 rounded-md border border-neutral-200 text-xs" data-testid={`recent-schriftje-${s.id}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-medium text-neutral-800">{formatDate(s.datum, { day: 'numeric', month: 'short' })}</span>
                  {s.stemming && (
                    <Badge variant="outline" className="text-[9px] h-4 px-1 border-neutral-200 capitalize">{s.stemming}</Badge>
                  )}
                </div>
                <div className="text-neutral-700 line-clamp-2">{s.activiteiten || s.hoogtepunt || s.notitie_voor_ouder}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Formulier */}
        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-neutral-100">
          <div><Label>Datum</Label>
            <Input type="date" value={form.datum} onChange={e => setForm({ ...form, datum: e.target.value })} data-testid="schriftje-datum" />
          </div>
          <div><Label>Stemming</Label>
            <Select value={form.stemming} onValueChange={v => setForm({ ...form, stemming: v })}>
              <SelectTrigger data-testid="schriftje-stemming"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {STEMMINGEN.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Eten</Label>
            <Input value={form.eten} onChange={e => setForm({ ...form, eten: e.target.value })} placeholder="Bijv. fruit en boterham" />
          </div>
          <div><Label>Slaap</Label>
            <Input value={form.slaap} onChange={e => setForm({ ...form, slaap: e.target.value })} placeholder="Bijv. 12:00 - 14:00" />
          </div>
          <div className="col-span-2"><Label>Activiteiten (dagverslag)</Label>
            <div className="flex items-center gap-2 mb-2">
              <VoiceRecorder onText={handleTranscribed} />
              <span className="text-[10px] text-neutral-500 flex-1">
                Spraak-invoer met Whisper. Bij 2+ kinderen open je multi-kind flow.
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="schriftje-sjabloon-chips">
              {[
                { label: "🥣 Heeft goed gegeten", text: `${kind.voornaam} heeft z'n bordje leeg gegeten en genoten. ` },
                { label: "😴 Fijn geslapen", text: `${kind.voornaam} heeft rustig geslapen. ` },
                { label: "🎨 Geknutseld", text: `We hebben samen geknutseld en ${kind.voornaam} had veel plezier. ` },
                { label: "🌳 Buitenspelen", text: `Lekker naar buiten geweest — ${kind.voornaam} was actief bezig. ` },
                { label: "🎶 Kringgesprek", text: `Tijdens de kring deed ${kind.voornaam} mee met liedjes en verhalen. ` },
                { label: "🤗 Sociaal", text: `${kind.voornaam} speelde fijn samen met de andere kinderen. ` },
              ].map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, activiteiten: (f.activiteiten || "") + s.text }))}
                  className="text-xs px-2 py-1 rounded-full border border-neutral-200 bg-neutral-50 hover:bg-emerald-50 hover:border-emerald-300 text-neutral-700 transition-colors"
                  data-testid={`schriftje-sjabloon-${i}`}
                >{s.label}</button>
              ))}
            </div>
            <Textarea rows={4} value={form.activiteiten}
              onChange={e => setForm({ ...form, activiteiten: e.target.value })}
              data-testid="schriftje-activiteiten"
              placeholder="Beschrijf kort wat je vandaag hebt gezien of gedaan met dit kind."
            />
          </div>
          <div><Label>Aantal verschoningen</Label>
            <Input type="number" min="0" value={form.verschoning_luier} onChange={e => setForm({ ...form, verschoning_luier: e.target.value })} />
          </div>
          <div><Label>Hoogtepunt</Label>
            <Input value={form.hoogtepunt} onChange={e => setForm({ ...form, hoogtepunt: e.target.value })} placeholder="Bijzonder moment vandaag" />
          </div>
          <div className="col-span-2"><Label>Notitie voor ouder (optioneel)</Label>
            <Textarea rows={2} value={form.notitie_voor_ouder} onChange={e => setForm({ ...form, notitie_voor_ouder: e.target.value })} />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-3 border-t border-neutral-100 text-[11px] text-neutral-500">
          <Sparkles className="h-3 w-3" /> AI-assistent gebruikt uitsluitend jouw invoer + de kindnaam. Geen verzonnen namen of activiteiten.
        </div>

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-neutral-100">
          <div className="text-[11px] text-neutral-500 flex items-center gap-1.5">
            <Camera className="h-3 w-3" /> Foto's alleen als er toestemming is
          </div>
          <PhotoUploadButton
            kindIds={[kind.id]}
            label="Foto toevoegen"
            data-testid="schriftje-photo-btn"
          />
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Sluiten</Button>
          <Button
            variant="outline"
            onClick={runAI}
            disabled={aiBusy || saving}
            data-testid="schriftje-ai-btn"
            className="border-[color:var(--terracotta)] text-[color:var(--terracotta)] hover:bg-[color:var(--terracotta)]/5"
          >
            <Sparkles className="h-4 w-4 mr-1.5" /> {aiBusy ? "Genereren…" : "AI-hulp"}
          </Button>
          <Button
            onClick={save}
            disabled={saving || !form.activiteiten.trim()}
            data-testid="schriftje-save-btn"
            style={{ background: "var(--sage)" }}
            className="text-white"
          >
            {saving ? "Verzenden…" : "Verzenden naar ouder"}
          </Button>
        </DialogFooter>
        <MultiKindSchriftjeDialog
          open={multiOpen}
          onClose={() => setMultiOpen(false)}
          transcript={multiTranscript}
          kinderen={groupKinderen}
          defaultDatum={form.datum}
        />
      </DialogContent>
    </Dialog>
  );
}
